import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Mail, Clock, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";

// Calendly URL — set REACT_APP_CALENDLY_URL in .env to enable embedded scheduling.
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || "";

// Calendly inline widget loader — loads widget.js once; the widget auto-mounts any
// element with class="calendly-inline-widget" and a data-url attribute.
const CALENDLY_SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const useCalendlyEmbed = (url) => {
  useEffect(() => {
    if (!url) return;
    if (!document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`)) {
      const script = document.createElement("script");
      script.src = CALENDLY_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    if (!document.querySelector('link[data-calendly-widget-css]')) {
      const css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = "https://assets.calendly.com/assets/external/widget.css";
      css.setAttribute("data-calendly-widget-css", "true");
      document.head.appendChild(css);
    }
  }, [url]);
};

export default function BookPage() {
  const { locale } = useI18n();
  useSeo({
    title: locale === "de"
      ? "Termin buchen — 30-Min Strategiegespräch | Joshi FusionTech Media"
      : "Book a call — 30-min Strategy Session | Joshi FusionTech Media",
    description: locale === "de"
      ? "Buche dein kostenloses 30-Minuten-Strategiegespräch. Berlin/Online — KI-Automatisierung, Webentwicklung und Marketing."
      : "Book your free 30-minute strategy call. Berlin/Online — AI automation, web development and marketing.",
    canonical: "https://www.joshifusiontech.de/book",
    locale,
  });

  // Append cosmetic params so the Calendly widget matches our brand.
  const calendlyUrl = CALENDLY_URL
    ? `${CALENDLY_URL}${CALENDLY_URL.includes("?") ? "&" : "?"}hide_gdpr_banner=1&primary_color=FF6A00&background_color=05050A&text_color=FAFAFF`
    : "";
  useCalendlyEmbed(calendlyUrl);

  const isDE = locale === "de";
  const copy = isDE
    ? {
        kicker: "Termin buchen",
        title: "Buche dein kostenloses 30-Minuten-Strategiegespräch.",
        subtitle: "Wähle einen passenden Termin im Kalender unten. Du erhältst sofort eine Bestätigung per E-Mail mit dem Meeting-Link.",
        what: "Was wir besprechen",
        bullets: [
          "Deine Ziele, Zielgruppe und aktuelle Herausforderungen",
          "Wie KI-Automatisierung & moderne Technologie helfen können",
          "Klare nächste Schritte mit transparenter Roadmap",
          "Investitionsrahmen und Zeitplan",
        ],
        altTitle: "Lieber direkt schreiben?",
        altSubtitle: "Wir antworten meist innerhalb eines Werktages.",
        email: "E-Mail",
        contactForm: "Kontaktformular",
        bookViaCalendly: "Termin in Calendly öffnen",
        notConfiguredTitle: "Online-Buchung wird in Kürze freigeschaltet.",
        notConfiguredText: "Bis dahin erreichst du uns gerne per E-Mail oder Kontaktformular — wir bestätigen deinen Wunschtermin innerhalb eines Werktages.",
        duration: "30 Min · kostenlos",
        timezone: "Zeitzone Europa/Berlin",
      }
    : {
        kicker: "Book a call",
        title: "Book your free 30-minute strategy call.",
        subtitle: "Pick a time in the calendar below. You'll receive an instant email confirmation with the meeting link.",
        what: "What we'll cover",
        bullets: [
          "Your goals, audience and current challenges",
          "How AI automation & modern tech can help",
          "Clear next steps with a transparent roadmap",
          "Investment range and timeline",
        ],
        altTitle: "Prefer to message us first?",
        altSubtitle: "We typically reply within one business day.",
        email: "Email",
        contactForm: "Contact form",
        bookViaCalendly: "Open Calendly in new tab",
        notConfiguredTitle: "Online scheduling will be live shortly.",
        notConfiguredText: "In the meantime, reach us via email or the contact form — we'll confirm your preferred time within one business day.",
        duration: "30 min · free",
        timezone: "Time zone: Europe/Berlin",
      };

  return (
    <main data-testid="book-page" className="pt-32">
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-brand-orange" />
            <span className="label-mono">{copy.kicker}</span>
          </div>
          <h1 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl tracking-tight text-white leading-[1.0]">
            {copy.title}
          </h1>
          <p className="mt-6 text-lg text-white/65 max-w-2xl leading-relaxed">{copy.subtitle}</p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-mono text-white/60">
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-brand-orange" /> {copy.duration}</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-orange" /> {copy.timezone}</span>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Calendar embed / fallback */}
          <div className="lg:col-span-8 glass rounded-3xl overflow-hidden relative min-h-[640px] flex flex-col">
            {CALENDLY_URL ? (
              <div
                data-testid="calendly-embed"
                className="calendly-inline-widget flex-1 w-full"
                data-url={calendlyUrl}
                style={{ minWidth: 320, minHeight: 720, background: "#05050A" }}
              />
            ) : (
              <div data-testid="booking-fallback" className="flex-1 p-10 md:p-14 flex flex-col justify-center">
                <div className="w-14 h-14 rounded-2xl bg-brand-orange/15 border border-brand-orange/30 flex items-center justify-center mb-6">
                  <Calendar className="w-6 h-6 text-brand-orange" />
                </div>
                <h2 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight">
                  {copy.notConfiguredTitle}
                </h2>
                <p className="mt-4 text-white/65 max-w-xl leading-relaxed">{copy.notConfiguredText}</p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
                  <a
                    href="mailto:info@joshifusiontech.de"
                    data-testid="book-email"
                    className="btn-secondary !py-3 !px-5 !text-sm"
                  >
                    <Mail className="w-4 h-4" /> {copy.email}
                  </a>
                  <Link
                    to="/contact"
                    data-testid="book-contact-form"
                    className="btn-primary !py-3 !px-5 !text-sm"
                  >
                    <Mail className="w-4 h-4" /> {copy.contactForm}
                  </Link>
                </div>
              </div>
            )}

            {CALENDLY_URL && (
              <div className="border-t border-white/[0.08] p-4 flex items-center justify-between bg-[#05050A]/60">
                <span className="label-mono text-white/40">{copy.timezone}</span>
                <a
                  href={CALENDLY_URL}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="book-open-calendly"
                  className="inline-flex items-center gap-1.5 text-xs font-mono text-brand-orange hover:underline"
                >
                  {copy.bookViaCalendly} <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 flex flex-col gap-5">
            <div className="glass rounded-3xl p-7">
              <h3 className="font-display font-bold text-xl text-white">{copy.what}</h3>
              <ul className="mt-5 space-y-3 text-sm text-white/75">
                {copy.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-brand-orange shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="glass rounded-3xl p-7">
              <h3 className="font-display font-bold text-base text-white">{copy.altTitle}</h3>
              <p className="mt-2 text-sm text-white/55">{copy.altSubtitle}</p>
              <div className="mt-5 space-y-3 text-sm">
                <a
                  href="mailto:info@joshifusiontech.de"
                  data-testid="book-sidebar-email"
                  className="flex items-center gap-3 text-white/80 hover:text-brand-orange transition-colors"
                >
                  <Mail className="w-4 h-4 text-brand-orange" />
                  <span>info@joshifusiontech.de</span>
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
